import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Chapter } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-amber-900/15 bg-white/90 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-deepteal">
    {children}
  </label>
);

const CONTACT_ROWS = [
  {
    icon: Phone,
    label: "Phone",
    value: "(410) 900-3947",
    href: "tel:+14109003947",
    testId: "contact-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: "Breland7490@gmail.com",
    href: "mailto:Breland7490@gmail.com",
    testId: "contact-email",
  },
  {
    icon: MapPin,
    label: "Address",
    value: "[ADD BUSINESS ADDRESS] · Halethorpe, Maryland",
    testId: "contact-address",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon–Sun · 6:00 AM – 6:00 PM",
    testId: "contact-hours",
  },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "General Question", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-deepteal py-24 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-amber-400/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Chapter num="08" label="Contact Us" light />
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-amber-50">
            Come see the puzzle <em className="italic text-amber-300">for yourself</em>.
          </h2>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-amber-100/80">
            Call, email, or send us a message — we'd love to answer your questions, talk about
            enrollment, and schedule a tour of our Halethorpe childcare home.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <Reveal delay={0.08}>
            <div className="flex h-full flex-col gap-4">
              <div className="rounded-3xl border border-amber-100/15 bg-deepteal-dark/60 p-7 backdrop-blur">
                <p className="font-serif text-2xl text-amber-50">Pieces to Our Puzzle Childcare</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300/80">
                  Halethorpe, Maryland
                </p>
                <ul className="mt-6 space-y-4">
                  {CONTACT_ROWS.map((r) => (
                    <li key={r.testId} className="flex items-center gap-4">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-300">
                        <r.icon className="h-4.5 w-4.5 h-4 w-4" strokeWidth={1.8} />
                      </span>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-100/50">{r.label}</p>
                        {r.href ? (
                          <a data-testid={r.testId} href={r.href} className="text-sm font-bold text-amber-50 hover:text-amber-300 transition-colors">
                            {r.value}
                          </a>
                        ) : (
                          <p data-testid={r.testId} className="text-sm font-bold text-amber-50">{r.value}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div
                data-testid="contact-map-placeholder"
                className="relative flex flex-1 min-h-[220px] items-center justify-center overflow-hidden rounded-3xl border border-amber-100/15 bg-[linear-gradient(135deg,#0A3540_0%,#0F4C5C_55%,#16637A_100%)]"
              >
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, rgba(254,243,199,0.4) 1px, transparent 0)", backgroundSize: "26px 26px" }} />
                <div className="relative text-center px-6">
                  <MapPin className="mx-auto h-9 w-9 text-amber-300" strokeWidth={1.6} />
                  <p className="mt-3 font-serif text-xl text-amber-50">Halethorpe, Maryland</p>
                  <p className="mt-1 text-xs text-amber-100/60 max-w-xs mx-auto">
                    Interactive map coming soon — the exact business address will be added here once provided.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <div className="rounded-3xl border border-amber-100/15 bg-parchment p-7 sm:p-9 shadow-2xl">
              {done ? (
                <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 className="h-9 w-9" />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-deepteal-dark">Message received!</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Thank you for reaching out. We'll respond as soon as we can — usually the same day.
                  </p>
                  <button
                    data-testid="contact-success-close"
                    onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", topic: "General Question", message: "", website: "" }); }}
                    className="mt-7 rounded-full border-2 border-deepteal/20 px-6 py-2.5 text-sm font-bold text-deepteal hover:border-terracotta hover:text-terracotta transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit}>
                  <p className="font-serif text-2xl text-deepteal-dark">Send Us a Message</p>
                  <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="ct-name">Your Name *</Label>
                      <input id="ct-name" data-testid="contact-form-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                    </div>
                    <div>
                      <Label htmlFor="ct-email">Email *</Label>
                      <input id="ct-email" data-testid="contact-form-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <Label htmlFor="ct-phone">Phone (optional)</Label>
                      <input id="ct-phone" data-testid="contact-form-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(410) 555-0123" />
                    </div>
                    <div>
                      <Label htmlFor="ct-topic">Topic</Label>
                      <select id="ct-topic" data-testid="contact-form-topic" value={form.topic} onChange={set("topic")} className={inputCls}>
                        <option>General Question</option>
                        <option>Schedule a Tour</option>
                        <option>Ask About Scholarships</option>
                        <option>Enrollment & Availability</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="ct-message">Message *</Label>
                      <textarea id="ct-message" data-testid="contact-form-message" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="How can we help your family?" />
                    </div>
                  </div>
                  <button
                    data-testid="contact-form-submit"
                    type="submit"
                    disabled={sending}
                    className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-deepteal px-7 py-4 text-sm font-bold text-amber-50 shadow-xl shadow-deepteal/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                    {sending ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
